import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, CreditCard, Phone, ArrowRight, ShieldCheck, CheckCircle } from 'lucide-react';
import { verifyIdentityApi } from '../api/auth';
import request from '../utils/request';
import DashboardLayout from '../layouts/DashboardLayout';
import Loading from '../components/Loading/Loading';
import '../styles/auth.scss';

function Verify() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', idCard: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [verified, setVerified] = useState(null);

  useEffect(() => {
    request.get('/auth/userInfo').then(res => {
      if (res.data && res.data.is_verified === 1) {
        setVerified({
          name: res.data.real_name,
          idCard: res.data.id_card,
          phone: res.data.verify_phone
        });
      } else {
        setVerified(false);
      }
    }).catch(() => {
      setVerified(false);
    }).finally(() => setPageLoading(false));
  }, []);

  const onChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = '请输入真实姓名';
    if (!form.idCard.trim()) errs.idCard = '请输入身份证号码';
    else if (!/^(\d{15}|\d{17}[\dXx])$/.test(form.idCard)) errs.idCard = '请输入正确的15位或18位身份证号码';
    if (!form.phone.trim()) errs.phone = '请输入手机号码';
    else if (!/^1[3-9]\d{9}$/.test(form.phone)) errs.phone = '手机号格式不正确';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await verifyIdentityApi(form);
      navigate('/');
    } catch (err) {
      const msg = err.message || '认证失败';
      if (msg.includes('身份证') || msg.includes('校验码') || msg.includes('出生') || msg.includes('省份') || msg.includes('地区码') || msg.includes('顺序码')) {
        setErrors({ idCard: msg });
      } else if (msg.includes('手机号')) {
        setErrors({ phone: msg });
      } else if (msg.includes('姓名')) {
        setErrors({ name: msg });
      } else {
        setErrors({ _global: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  // 已认证展示
  const renderVerified = () => (
    <div className="auth-page auth-page--inner">
      <span className="auth-badge">VERIFIED</span>
      <h1 className="auth-title">已完成实名认证</h1>
      <p className="auth-desc">您的实名信息已通过核验，以下为脱敏后的认证信息。</p>

      <div className="auth-card">
        <div className="verify-success-icon">
          <CheckCircle size={48} color="#22c55e" />
        </div>
        <div className="verify-info-list">
          <div className="verify-info-item">
            <User size={16} className="verify-info-icon" />
            <span className="verify-info-label">姓名</span>
            <span className="verify-info-value">{verified.name}</span>
          </div>
          <div className="verify-info-item">
            <CreditCard size={16} className="verify-info-icon" />
            <span className="verify-info-label">身份证号</span>
            <span className="verify-info-value">{verified.idCard}</span>
          </div>
          <div className="verify-info-item">
            <Phone size={16} className="verify-info-icon" />
            <span className="verify-info-label">手机号</span>
            <span className="verify-info-value">{verified.phone}</span>
          </div>
        </div>
        <span className="auth-link" onClick={() => navigate(-1)}>返回</span>
      </div>
    </div>
  );

  // 未认证表单
  const renderForm = () => (
    <div className="auth-page auth-page--inner">
      <span className="auth-badge">SECURE AUTHENTICATION</span>
      <h1 className="auth-title">实名认证</h1>
      <p className="auth-desc">为了保障您的账号安全和合规使用，请完成实名信息核验。</p>

      <form className="auth-card" onSubmit={handleSubmit}>
        {errors._global && <div className="auth-field-error" style={{ marginBottom: 16, textAlign: 'center' }}>{errors._global}</div>}
        <div className="auth-field">
          <label className="auth-field-label">姓名 (NAME)</label>
          <div className="auth-field-input-wrap">
            <User size={18} className="field-icon" />
            <input type="text" placeholder="请输入您的真实姓名" value={form.name} onChange={e => onChange('name', e.target.value)} />
          </div>
          {errors.name && <div className="auth-field-error">{errors.name}</div>}
        </div>

        <div className="auth-field">
          <label className="auth-field-label">身份证号码 (ID CARD NUMBER)</label>
          <div className="auth-field-input-wrap">
            <CreditCard size={18} className="field-icon" />
            <input type="text" placeholder="请输入18位身份证号码" maxLength={18} value={form.idCard} onChange={e => onChange('idCard', e.target.value)} />
          </div>
          {errors.idCard && <div className="auth-field-error">{errors.idCard}</div>}
        </div>

        <div className="auth-field">
          <label className="auth-field-label">电话号码 (PHONE NUMBER)</label>
          <div className="auth-field-input-wrap">
            <Phone size={18} className="field-icon" />
            <input type="tel" placeholder="请输入您的手机号码" value={form.phone} onChange={e => onChange('phone', e.target.value)} />
          </div>
          {errors.phone && <div className="auth-field-error">{errors.phone}</div>}
        </div>

        <div className="auth-agreement">
          <ShieldCheck size={18} className="auth-agreement-icon checked" />
          <span className="auth-agreement-text">您的信息仅用于实名认证，我们将严格保密。</span>
        </div>

        <button className="auth-submit" type="submit" disabled={loading}>
          {loading ? '提交中...' : '提交认证'} <ArrowRight size={18} />
        </button>

        <span className="auth-link" onClick={() => navigate(-1)}>返回</span>
      </form>
    </div>
  );

  return (
    <DashboardLayout>
      {pageLoading ? <Loading text="加载中..." /> : verified ? renderVerified() : renderForm()}
    </DashboardLayout>
  );
}

export default Verify;
